import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { container } from '@infrastructure/container';
import { Collection } from '@domain/product/models/Collection';
import {
  CreateCollectionDTO,
  UpdateCollectionDTO,
} from '@infrastructure/product/dto/CollectionDTO';

export const useCollections = () => {
  const queryClient = useQueryClient();

  const {
    data: collectionsResponse,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: ['collections'],
    queryFn: async () => {
      const result = await container.collectionRepository.getCollections();
      if (result.status === 'error') throw new Error(result.error.message);
      return result;
    },
    refetchOnWindowFocus: false,
  });

  const invalidateCollections = () => {
    queryClient.invalidateQueries({ queryKey: ['collections'] });
    queryClient.invalidateQueries({ queryKey: ['collection'] });
    queryClient.invalidateQueries({ queryKey: ['products'] });
  };

  const { mutateAsync: createCollection } = useMutation({
    mutationFn: async (data: CreateCollectionDTO) => {
      const result = await container.collectionRepository.createCollection(data);
      if (result.status === 'error') throw new Error(result.error.message);
      return result.data;
    },
    onSuccess: invalidateCollections,
  });

  const { mutateAsync: updateCollection } = useMutation({
    mutationFn: async ({
      collectionId,
      data,
    }: {
      collectionId: string;
      data: UpdateCollectionDTO;
    }) => {
      const result = await container.collectionRepository.updateCollection(
        collectionId,
        data
      );
      if (result.status === 'error') throw new Error(result.error.message);
      return result.data;
    },
    onSuccess: invalidateCollections,
  });

  const { mutateAsync: deleteCollection, isPending: isDeleting } = useMutation({
    mutationFn: async (collectionId: string) => {
      const result = await container.collectionRepository.deleteCollection(
        collectionId
      );
      if (result.status === 'error') throw new Error(result.error.message);
    },
    onSuccess: invalidateCollections,
  });

  const { mutateAsync: addProductsToCollection } = useMutation({
    mutationFn: async ({
      collectionId,
      productIds,
    }: {
      collectionId: string;
      productIds: string[];
    }) => {
      const result = await container.collectionRepository.addProductsToCollection(
        collectionId,
        productIds
      );
      if (result.status === 'error') throw new Error(result.error.message);
      return result.data;
    },
    onSuccess: invalidateCollections,
  });

  const { mutateAsync: removeProductsFromCollection } = useMutation({
    mutationFn: async ({
      collectionId,
      productIds,
    }: {
      collectionId: string;
      productIds: string[];
    }) => {
      const result =
        await container.collectionRepository.removeProductsFromCollection(
          collectionId,
          productIds
        );
      if (result.status === 'error') throw new Error(result.error.message);
    },
    onSuccess: invalidateCollections,
  });

  return {
    collections: collectionsResponse,
    isLoading,
    isFetching,
    createCollection,
    updateCollection,
    deleteCollection,
    isDeleting,
    addProductsToCollection,
    removeProductsFromCollection,
  };
};

export const useCollection = (collectionId?: string) => {
  const queryClient = useQueryClient();

  const { data: collection, isLoading } = useQuery({
    queryKey: ['collection', collectionId],
    queryFn: async () => {
      if (!collectionId) throw new Error('Collection ID is required');
      const result =
        await container.collectionRepository.getCollection(collectionId);
      if (result.status === 'error') throw new Error(result.error.message);
      return result.data;
    },
    enabled: !!collectionId,
  });

  return { collection, isLoading };
};
